import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Video, User, Mail, Phone, MessageSquare, Check, AlertCircle, MessageCircle, Briefcase } from 'lucide-react';
import axios from 'axios';

const BookingForm = ({ isOpen, onClose, t, language, activityType = 'general' }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        contactMethod: 'whatsapp',
        date: '',
        time: '',
        platform: 'zoom',
        message: '',
        activityType: 'general'
    });

    const [status, setStatus] = useState('idle'); // idle, submitting, success, error
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: '',
                email: '',
                phone: '',
                contactMethod: 'whatsapp',
                date: '',
                time: '',
                platform: 'zoom',
                message: '',
                activityType: activityType
            });
            setStatus('idle');
            setErrors({});
        }
    }, [isOpen]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone is required';
        }
        
        if (!formData.date) {
            newErrors.date = 'Date is required';
        }
        
        if (!formData.time) {
            newErrors.time = 'Time is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setStatus('submitting');

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_HOST}/api/bookings`, {
                ...formData,
                datetime: `${formData.date} ${formData.time}`,
                language: language || 'en'
            });

            if (response.data.success) {
                setStatus('success');
                // Reset form after 2 seconds
                setTimeout(() => {
                    setFormData({
                        name: '',
                        email: '',
                        phone: '',
                        contactMethod: 'whatsapp',
                        date: '',
                        time: '',
                        platform: 'zoom',
                        message: '',
                        activityType: 'general'
                    });
                    setStatus('idle');
                    onClose();
                }, 5000);
            } else {
                setStatus('error');
            }
        } catch (error) {
            console.error('Booking error:', error);
            setStatus('error');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const getMinDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    const getTimeSlots = () => {
        const slots = [];
        for (let hour = 9; hour <= 18; hour++) {
            slots.push(`${hour.toString().padStart(2, '0')}:00`);
            if (hour < 18) {
                slots.push(`${hour.toString().padStart(2, '0')}:30`);
            }
        }
        return slots;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fadeInFast">
            <div className="relative bg-ivory rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl transform animate-fadeInFast">
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-600 to-teal text-ivory p-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="relative z-10">
                        <button
                            onClick={onClose}
                            className="absolute top-0 right-0 p-2 hover:bg-ivory/20 rounded-full transition-all duration-300 hover:scale-110 group"
                        >
                            <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300"/>
                        </button>
                        
                        <div className="pr-12">
                            <h2 className="text-2xl lg:text-3xl font-bold mb-2">
                                {t('booking.title')}
                            </h2>
                            <p className="text-ivory/90">
                                {t('booking.subtitle')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form Content */}
                <div className="p-6 max-h-[calc(90vh-140px)] overflow-y-auto">
                    {status === 'success' ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                                <Check className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-emerald-600 mb-2">
                                Success!
                            </h3>
                            <p className="text-taupe/80">
                                {t('booking.form.success')}
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Name Field */}
                            <div>
                                <label className="block text-sm font-medium text-taupe mb-2">
                                    <User className="w-4 h-4 inline mr-2" />
                                    {t('booking.form.name')}
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder={t('booking.form.namePlaceholder')}
                                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                                        errors.name ? 'border-red-300 bg-red-50' : 'border-blush/30 bg-soft-cream'
                                    }`}
                                    disabled={status === 'submitting'}
                                />
                                {errors.name && (
                                    <p className="text-red-500 text-sm mt-1 flex items-center">
                                        <AlertCircle className="w-4 h-4 mr-1" />
                                        {errors.name}
                                    </p>
                                )}
                            </div>

                            {/* Email Field */}
                            <div>
                                <label className="block text-sm font-medium text-taupe mb-2">
                                    <Mail className="w-4 h-4 inline mr-2" />
                                    {t('booking.form.email')}
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder={t('booking.form.emailPlaceholder')}
                                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                                        errors.email ? 'border-red-300 bg-red-50' : 'border-blush/30 bg-soft-cream'
                                    }`}
                                    disabled={status === 'submitting'}
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-sm mt-1 flex items-center">
                                        <AlertCircle className="w-4 h-4 mr-1" />
                                        {errors.email}
                                    </p>
                                )}
                            </div>

                            {/* Phone Field */}
                            <div>
                                <label className="block text-sm font-medium text-taupe mb-2">
                                    <Phone className="w-4 h-4 inline mr-2" />
                                    {t('booking.form.phone')}
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder={t('booking.form.phonePlaceholder')}
                                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                                        errors.phone ? 'border-red-300 bg-red-50' : 'border-blush/30 bg-soft-cream'
                                    }`}
                                    disabled={status === 'submitting'}
                                />
                                {errors.phone && (
                                    <p className="text-red-500 text-sm mt-1 flex items-center">
                                        <AlertCircle className="w-4 h-4 mr-1" />
                                        {errors.phone}
                                    </p>
                                )}
                            </div>

                            {/* Activity Type Selection */}
                            <div>
                                <label className="block text-sm font-medium text-taupe mb-2">
                                    <Briefcase className="w-4 h-4 inline mr-2" />
                                    {t('booking.form.activityType')}
                                </label>
                                <select
                                    name="activityType"
                                    value={formData.activityType}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 rounded-xl border border-blush/30 bg-soft-cream transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-600"
                                    disabled={status === 'submitting'}
                                >
                                    <option value="general">{t('booking.form.activityTypeOptions.general')}</option>
                                    <option value="neuropsychology">{t('booking.form.activityTypeOptions.neuropsychology')}</option>
                                    <option value="familyPsychologist">{t('booking.form.activityTypeOptions.familyPsychologist')}</option>
                                    <option value="childPsychologist">{t('booking.form.activityTypeOptions.childPsychologist')}</option>
                                    <option value="giftedChildren">{t('booking.form.activityTypeOptions.giftedChildren')}</option>
                                </select>
                            </div>

                            {/* Contact Method Selection */}
                            <div>
                                <label className="block text-sm font-medium text-taupe mb-3">
                                    <MessageCircle className="w-4 h-4 inline mr-2" />
                                    {t('booking.form.contactMethod')}
                                </label>
                                <div className="grid grid-cols-3 gap-3">
                                    {['whatsapp', 'telegram', 'phone'].map((method) => (
                                        <label key={method} className="cursor-pointer">
                                            <input
                                                type="radio"
                                                name="contactMethod"
                                                value={method}
                                                checked={formData.contactMethod === method}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                                disabled={status === 'submitting'}
                                            />
                                            <div className={`p-3 rounded-xl border-2 transition-all duration-300 text-center hover:scale-105 ${
                                                formData.contactMethod === method
                                                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                                                    : 'border-blush/30 bg-soft-cream text-taupe hover:border-emerald-300'
                                            }`}>
                                                <MessageCircle className="w-5 h-5 mx-auto mb-1" />
                                                <span className="text-sm font-medium">
                                                    {t(`booking.form.contactMethodOptions.${method}`)}
                                                </span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Date and Time Row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Date Field */}
                                <div>
                                    <label className="block text-sm font-medium text-taupe mb-2">
                                        <Calendar className="w-4 h-4 inline mr-2" />
                                        {t('booking.form.date')}
                                    </label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        min={getMinDate()}
                                        className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                                            errors.date ? 'border-red-300 bg-red-50' : 'border-blush/30 bg-soft-cream'
                                        }`}
                                        disabled={status === 'submitting'}
                                    />
                                    {errors.date && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {errors.date}
                                        </p>
                                    )}
                                </div>

                                {/* Time Field */}
                                <div>
                                    <label className="block text-sm font-medium text-taupe mb-2">
                                        <Clock className="w-4 h-4 inline mr-2" />
                                        {t('booking.form.time')}
                                    </label>
                                    <select
                                        name="time"
                                        value={formData.time}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                                            errors.time ? 'border-red-300 bg-red-50' : 'border-blush/30 bg-soft-cream'
                                        }`}
                                        disabled={status === 'submitting'}
                                    >
                                        <option value="">{t('booking.form.timePlaceholder')}</option>
                                        {getTimeSlots().map(time => (
                                            <option key={time} value={time}>{time}</option>
                                        ))}
                                    </select>
                                    {errors.time && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {errors.time}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Platform Selection */}
                            <div>
                                <label className="block text-sm font-medium text-taupe mb-3">
                                    <Video className="w-4 h-4 inline mr-2" />
                                    {t('booking.form.platform')}
                                </label>
                                <div className="grid grid-cols-3 gap-3">
                                    {['zoom', 'googlemeet', 'yandex'].map((platform) => (
                                        <label key={platform} className="cursor-pointer">
                                            <input
                                                type="radio"
                                                name="platform"
                                                value={platform}
                                                checked={formData.platform === platform}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                                disabled={status === 'submitting'}
                                            />
                                            <div className={`p-3 rounded-xl border-2 transition-all duration-300 text-center hover:scale-105 ${
                                                formData.platform === platform
                                                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                                                    : 'border-blush/30 bg-soft-cream text-taupe hover:border-emerald-300'
                                            }`}>
                                                <Video className="w-5 h-5 mx-auto mb-1" />
                                                <span className="text-sm font-medium">
                                                    {t(`booking.form.platformOptions.${platform}`)}
                                                </span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Message Field */}
                            <div>
                                <label className="block text-sm font-medium text-taupe mb-2">
                                    <MessageSquare className="w-4 h-4 inline mr-2" />
                                    {t('booking.form.message')}
                                </label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder={t('booking.form.messagePlaceholder')}
                                    rows={4}
                                    className="w-full px-4 py-3 rounded-xl border border-blush/30 bg-soft-cream transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-600 resize-none"
                                    disabled={status === 'submitting'}
                                />
                            </div>

                            {/* Submit Button */}
                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={status === 'submitting'}
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-ivory px-6 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl relative overflow-hidden group"
                                >
                                    <span className="relative z-10">
                                        {status === 'submitting' ? t('booking.form.submitting') : t('booking.form.submit')}
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-700 to-teal opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </button>
                                
                                {status === 'error' && (
                                    <p className="text-red-500 text-sm mt-2 text-center flex items-center justify-center">
                                        <AlertCircle className="w-4 h-4 mr-1" />
                                        {t('booking.form.error')}
                                    </p>
                                )}
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookingForm;